import { BlueprintV2 } from '@editor/interfaces/blueprint'
import { InspectionArea } from '@editor/interfaces/inspection'
import { SignedUrls } from '@editor/pages/projects/pcd-alignment/definitions'

import { getSignedUrlForPotreeFiles } from './InspectionArea'

export interface LoaderParams {
  inspectionArea: InspectionArea
  blueprint?: BlueprintV2
  blueprintAreaId?: string
}

export class PotreeCache {
  /**
   * A map of area IDs to signed URLs.
   */
  private cache: Map<string, Awaited<SignedUrls>>

  /**
   * Default expiration time for the cache.
   * This will be overridden by the actual expiration time when the URLs are fetched.
   * Value is in seconds.
   */
  ttl: number = 60 // 1 minute

  /**
   * Buffer time to avoid expiration.
   * This is used to expire the URLs earlier than the actual expiration time to avoid issues with time differences.
   * Value is in seconds.
   */
  ttlBuffer: number = 10

  /**
   * Cached access token for Auth0.
   * This is used to avoid fetching a new token every time.
   */
  private auth0AccessToken: string | null = null

  /**
   * Function to get the access token.
   */
  private getAccessToken: () => Promise<string>

  constructor(getAccessToken: () => Promise<string>) {
    this.cache = new Map()
    this.getAccessToken = getAccessToken
  }

  /**
   * Type guard to check if the Potree file exists in the signed URLs.
   *
   * @param fileName File name to check
   * @param urls Signed URLs for Potree files
   * @returns Whether the file exists
   */
  static hasFile(fileName: string, urls: Awaited<SignedUrls> | null): fileName is keyof Awaited<SignedUrls> {
    return (urls && fileName in urls) || false
  }

  /**
   * Checks the cached access token for Potree, and if it is not available, fetches a new one.
   *
   * @param getAccessToken Function to get the access token
   * @returns Auth0 access token
   */
  async getAuth0AccessToken(): Promise<string> {
    // Check the expiration of existing token, if exists

    if (this.auth0AccessToken) {
      const token = JSON.parse(atob(this.auth0AccessToken.split('.')[1])) as { exp: number }
      if ((token.exp - this.ttlBuffer) * 1000 > Date.now()) {
        return this.auth0AccessToken
      }
    }

    // Fetch new token
    this.auth0AccessToken = await this.getAccessToken()
    return this.auth0AccessToken
  }

  /**
   * Given the signed URLs returned from blueprint's get-potree-files API,
   * extract the blueprint-specific area URLs from it.
   *
   * @returns Signed URLs for the blueprint area files, or null if not available.
   */
  static getBlueprintAreaUrls(signedURLs: Awaited<SignedUrls>, blueprintAreaId: string): Awaited<SignedUrls> {
    const urls = signedURLs?.aligned_pcds?.[blueprintAreaId]
    if (!urls) {
      return null
    }

    return {
      ...signedURLs,
      metadata: urls.metadata,
      hierarchy: urls.hierarchy,
      octree: urls.octree,
    }
  }

  /**
   * Generate signed URLs for Potree files and cache them for a minute.
   *
   * @param token Auth0 access token
   * @param projectId Project ID
   * @param inspectionAreaId Inspection Area ID
   * @param showErrorModal Function to show error modal
   * @returns Signed URLs for Potree files
   */
  async getPotreeSignedURLs(
    projectId: string,
    { inspectionArea, blueprint, blueprintAreaId }: LoaderParams,
    showErrorModal: (message: string) => void,
  ): SignedUrls {
    if (!this.auth0AccessToken) {
      this.auth0AccessToken = await this.getAuth0AccessToken()
    }

    const cacheId = [projectId, inspectionArea.inspection_area_id, blueprint?.blueprint_id].filter(Boolean).join('-')
    let potreeSignedUrls = this.cache.get(cacheId)

    if (potreeSignedUrls && potreeSignedUrls.expiry.getTime() > Date.now()) {
      if (blueprintAreaId) {
        return PotreeCache.getBlueprintAreaUrls(potreeSignedUrls, blueprintAreaId)
      }
      return potreeSignedUrls
    }

    const urls = await getSignedUrlForPotreeFiles(
      this.auth0AccessToken,
      projectId,
      inspectionArea.inspection_area_id,
      blueprint?.blueprint_id || null,
      showErrorModal,
    )
    if (!urls) return null

    const url = new URL(urls.metadata)
    const expiry = parseInt(url.searchParams.get('X-Amz-Expires') || '', 10) || this.ttl

    // expire earlier to avoid the time difference between fetching this URL and using it
    potreeSignedUrls = {
      ...urls,
      expiry: new Date(Date.now() + 1000 * (expiry - this.ttlBuffer)),
      inspectionAreaId: inspectionArea.inspection_area_id,
      blueprintId: blueprint?.blueprint_id,
    }

    this.cache.set(cacheId, potreeSignedUrls)

    if (blueprintAreaId) return PotreeCache.getBlueprintAreaUrls(potreeSignedUrls, blueprintAreaId)
    return potreeSignedUrls
  }
}
